import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchApi } from '@/lib/api'
import { createReviewSocket, type ReviewEvent } from '@/lib/reviewWs'
import { createPrSummarySocket, type PrSummaryEvent } from '@/lib/prSummaryWs'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import {
  GitPullRequestArrow, Play, XCircle, History,
  CheckCircle, AlertCircle, Loader2, MinusCircle,
  Clock, ChevronDown, ChevronRight,
  ExternalLink, MessageSquare, Merge, SkipForward,
  Wrench, RefreshCw, Zap, RotateCcw, Eye,
  Shield, Target, AlertTriangle, ArrowRight,
  Ban, CircleAlert, Bell, X, FileText,
  GitBranch, User, Send,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/date'

// --- Types ---

type ReviewConfig = {
  repos: string[]
  steps: string[]
}

type StepData = {
  status: string
  detail: string
  error: string
  review_summary: string | null
  review_severity: string | null
  human_question: string | null
  human_answer: string | null
  merge_sha: string | null
  diagnosis: string | null
}

type PrInfo = {
  pr_url: string | null
  pr_title: string | null
  pr_author: string | null
  pr_branch: string | null
  pr_base: string | null
}

type SessionDetail = {
  id: number
  status: string
  repos: string[]
  total_prs: number
  merged_prs: number
  skipped_prs: number
  failed_prs: number
  auto_merge: boolean
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
  step_matrix: Record<string, Record<number, Record<string, StepData>>>
  pr_info: Record<string, Record<number, PrInfo>>
}

type SessionSummary = {
  id: number
  status: string
  repos: string[]
  total_prs: number
  merged_prs: number
  skipped_prs: number
  failed_prs: number
  auto_merge: boolean
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
}

type WaitingJudgmentData = {
  repo: string
  prNumber: number
  question: string
  reviewSummary: string | null
  reviewSeverity: string | null
  ciPassed: boolean
  mergeable: boolean
  diagnosis: string | null
  isReReview: boolean
  fixSummary: string | null
  ciTimedOut: boolean
}

// Extract waiting judgments from REST step_matrix data
function extractWaitingJudgments(
  matrix: Record<string, Record<number, Record<string, StepData>>>,
): Record<string, WaitingJudgmentData> {
  const judgments: Record<string, WaitingJudgmentData> = {}
  for (const [repo, prs] of Object.entries(matrix)) {
    for (const [prNumStr, steps] of Object.entries(prs)) {
      const mergeStep = steps.merge
      if (mergeStep?.status === 'waiting_human') {
        const prNumber = Number(prNumStr)
        const key = `${repo}:${prNumber}`
        const reviewStep = steps.code_review
        let ciPassed = true
        let mergeable = true
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let diag: any = null
        if (mergeStep.diagnosis) {
          try {
            diag = JSON.parse(mergeStep.diagnosis)
            ciPassed = diag.ci_passed ?? true
            mergeable = diag.mergeable ?? true
          } catch {
            // ignore parse error
          }
        }
        judgments[key] = {
          repo,
          prNumber,
          question: mergeStep.human_question ?? '',
          reviewSummary: reviewStep?.review_summary ?? null,
          reviewSeverity: reviewStep?.review_severity ?? null,
          ciPassed,
          mergeable,
          diagnosis: mergeStep.diagnosis ?? null,
          isReReview: diag?.is_re_review ?? false,
          fixSummary: diag?.fix_summary_excerpt ?? null,
          ciTimedOut: diag?.ci_timed_out ?? false,
        }
      }
    }
  }
  return judgments
}

type PrSummaryJson = {
  purpose?: string
  key_changes?: string[]
  impact_scope?: string
  risk_level?: string
  risk_details?: string
  dependencies?: string
  review_points?: string[]
  one_line_summary?: string
}

type PrSummaryRecord = {
  id: number
  repo_name: string
  pr_number: number
  pr_title: string | null
  pr_author: string | null
  pr_branch: string | null
  pr_base: string | null
  pr_url: string | null
  head_sha: string
  pr_updated_at: string | null
  summary_text: string | null
  summary_json: PrSummaryJson | null
  evaluation_status: string
  mergeable_state: string | null
  ci_status: string | null
  evaluated_at: string | null
  created_at: string
  updated_at: string
}

type SummariesResponse = {
  repos: Record<string, PrSummaryRecord[]>
  total: number
  scheduler: { running: boolean; last_run: string | null }
}

// --- Constants ---

const STEP_LABELS: Record<string, string> = {
  fetch_info: 'Info',
  code_review: 'Review',
  ci_check: 'CI',
  conflict_check: 'Conflict',
  merge: 'Merge',
}

const SEVERITY_COLORS: Record<string, string> = {
  clean: 'bg-green-500/20 text-green-400',
  minor: 'bg-yellow-500/20 text-yellow-400',
  major: 'bg-orange-500/20 text-orange-400',
  critical: 'bg-red-500/20 text-red-400',
}

const RISK_COLORS: Record<string, string> = {
  low: 'bg-green-500/20 text-green-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  high: 'bg-red-500/20 text-red-400',
}

const EVAL_STATUS_MAP: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-gray-500/20 text-gray-400' },
  running: { label: 'Running', cls: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Done', cls: 'bg-green-500/20 text-green-400' },
  partial: { label: 'Partial', cls: 'bg-orange-500/20 text-orange-400' },
  failed: { label: 'Failed', cls: 'bg-red-500/20 text-red-400' },
  stale: { label: 'Stale', cls: 'bg-yellow-500/20 text-yellow-400' },
}

// --- Helper components ---

function StepIcon({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return <CheckCircle size={16} className="text-green-400" />
    case 'running':
      return <Loader2 size={16} className="text-blue-400 animate-spin" />
    case 'failed':
      return <AlertCircle size={16} className="text-red-400" />
    case 'skipped':
      return <MinusCircle size={16} className="text-yellow-500/60" />
    case 'waiting_human':
      return <MessageSquare size={16} className="text-purple-400 animate-pulse" />
    default:
      return <Clock size={16} className="text-[var(--color-text-muted)]/40" />
  }
}

function stepBgClass(status: string): string {
  switch (status) {
    case 'success': return 'bg-green-500/10'
    case 'running': return 'bg-blue-500/10'
    case 'failed': return 'bg-red-500/10'
    case 'skipped': return 'bg-yellow-500/5'
    case 'waiting_human': return 'bg-purple-500/10'
    default: return ''
  }
}

function sessionStatusBadge(status: string) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Pending', cls: 'bg-gray-500/20 text-gray-400' },
    running: { label: 'Running', cls: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Completed', cls: 'bg-green-500/20 text-green-400' },
    failed: { label: 'Failed', cls: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Cancelled', cls: 'bg-yellow-500/20 text-yellow-400' },
    interrupted: { label: 'Interrupted', cls: 'bg-orange-500/20 text-orange-400' },
  }
  const s = map[status] ?? map.pending
  return <span className={cn('px-2 py-0.5 rounded text-xs font-medium', s.cls)}>{s.label}</span>
}

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', SEVERITY_COLORS[severity] ?? 'bg-gray-500/20 text-gray-400')}>
      {severity}
    </span>
  )
}

function EvalStatusBadge({ status }: { status: string }) {
  const s = EVAL_STATUS_MAP[status] ?? EVAL_STATUS_MAP.pending
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-1', s.cls)}>
      {status === 'running' && <Loader2 size={10} className="animate-spin" />}
      {s.label}
    </span>
  )
}

function RiskBadge({ level }: { level: string }) {
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', RISK_COLORS[level] ?? 'bg-gray-500/20 text-gray-400')}>
      {level}
    </span>
  )
}

function MergeableBadge({ state }: { state: string | null }) {
  if (!state || state === 'clean' || state === 'unknown') return null
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    dirty: { label: 'Conflict', cls: 'bg-red-500/20 text-red-400', icon: <Ban size={10} /> },
    behind: { label: 'Behind', cls: 'bg-yellow-500/20 text-yellow-400', icon: <CircleAlert size={10} /> },
    blocked: { label: 'Blocked', cls: 'bg-orange-500/20 text-orange-400', icon: <CircleAlert size={10} /> },
    unstable: { label: 'Unstable', cls: 'bg-orange-500/20 text-orange-400', icon: <CircleAlert size={10} /> },
  }
  const s = map[state]
  if (!s) return null
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-0.5', s.cls)}>
      {s.icon} {s.label}
    </span>
  )
}

function CiBadge({ status }: { status: string | null }) {
  if (!status) return null
  const map: Record<string, { cls: string; icon: React.ReactNode }> = {
    success: { cls: 'text-green-400', icon: <CheckCircle size={12} /> },
    pending: { cls: 'text-yellow-400', icon: <Loader2 size={12} className="animate-spin" /> },
    failure: { cls: 'text-red-400', icon: <XCircle size={12} /> },
    error: { cls: 'text-red-400', icon: <XCircle size={12} /> },
  }
  const s = map[status] ?? map.pending
  return (
    <span className={cn('inline-flex items-center', s.cls)} title={`CI: ${status}`}>
      {s.icon}
    </span>
  )
}

// --- PR Overview Tab ---

function PrOverview() {
  const [summaries, setSummaries] = useState<SummariesResponse | null>(null)
  const [evalStatus, setEvalStatus] = useState<string>('')
  const [evalProgress, setEvalProgress] = useState({ total: 0, completed: 0, failed: 0 })
  const [evalCurrent, setEvalCurrent] = useState<{ repo: string; pr_number: number; pr_title: string } | null>(null)
  const [streamContent, setStreamContent] = useState<Record<string, string>>({})
  const [expandedRepos, setExpandedRepos] = useState<Set<string>>(new Set())
  const [expandedPr, setExpandedPr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const socketRef = useRef<ReturnType<typeof createPrSummarySocket> | null>(null)

  const loadSummaries = useCallback(async () => {
    try {
      const data = await fetchApi<SummariesResponse>('/pr-summary/summaries')
      setSummaries(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load summaries')
    }
  }, [])

  useEffect(() => {
    loadSummaries()
  }, [loadSummaries])

  const connectWs = useCallback(() => {
    if (socketRef.current) return

    const socket = createPrSummarySocket(
      (event: PrSummaryEvent) => {
        if (event.type === 'pr_summary_eval_status') {
          setEvalStatus(event.status ?? '')
          if (['completed', 'failed', 'cancelled'].includes(event.status ?? '')) {
            setLoading(false)
            setEvalCurrent(null)
            // Reload summaries after completion
            loadSummaries()
          }
        } else if (event.type === 'pr_summary_eval_progress') {
          setEvalProgress({
            total: event.total ?? 0,
            completed: event.completed ?? 0,
            failed: event.failed ?? 0,
          })
        } else if (event.type === 'pr_summary_eval_current') {
          setEvalCurrent({
            repo: event.repo ?? '',
            pr_number: event.pr_number ?? 0,
            pr_title: event.pr_title ?? '',
          })
        } else if (event.type === 'pr_summary_stream' && event.repo && event.pr_number) {
          const key = `${event.repo}:${event.pr_number}`
          if (event.content) {
            setStreamContent((prev) => ({
              ...prev,
              [key]: (prev[key] ?? '') + event.content!,
            }))
          }
        }
      },
      () => { socketRef.current = null },
    )
    socketRef.current = socket
  }, [loadSummaries])

  useEffect(() => {
    return () => {
      socketRef.current?.close()
    }
  }, [])

  const refreshPrs = async () => {
    setError('')
    setLoading(true)
    try {
      await fetchApi('/pr-summary/refresh', { method: 'POST' })
      await loadSummaries()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to refresh')
    } finally {
      setLoading(false)
    }
  }

  const startEvaluation = async (mode: 'diff' | 'full') => {
    setError('')
    setLoading(true)
    setStreamContent({})
    setEvalCurrent(null)
    setEvalProgress({ total: 0, completed: 0, failed: 0 })
    connectWs()

    try {
      const result = await fetchApi<{ status: string; message?: string }>(
        '/pr-summary/evaluate',
        { method: 'POST', body: JSON.stringify({ mode }) },
      )
      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start evaluation')
      setLoading(false)
    }
  }

  const cancelEvaluation = async () => {
    try {
      await fetchApi('/pr-summary/evaluate/cancel', { method: 'POST' })
    } catch { /* ignore */ }
  }

  const toggleRepoExpand = (repo: string) => {
    setExpandedRepos((prev) => {
      const next = new Set(prev)
      if (next.has(repo)) next.delete(repo)
      else next.add(repo)
      return next
    })
  }

  const isEvaluating = evalStatus === 'running' || evalStatus === 'fetching' || loading
  const repos = summaries?.repos ?? {}
  const repoNames = Object.keys(repos).sort()

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center justify-between mb-3 shrink-0 flex-wrap gap-2">
        <div className="flex items-center gap-3 text-sm text-[var(--color-text-muted)]">
          <span>Total: <strong className="text-[var(--color-text)]">{summaries?.total ?? 0}</strong> PRs</span>
          {summaries?.scheduler?.last_run && (
            <span className="text-[10px]">
              Last auto-check: {formatDateTime(summaries.scheduler.last_run)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEvaluating && (
            <button
              onClick={cancelEvaluation}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <XCircle size={14} />
              Cancel
            </button>
          )}
          <button
            onClick={refreshPrs}
            disabled={isEvaluating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] transition-colors disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading && !evalStatus ? 'animate-spin' : ''} />
            PR Update
          </button>
          <button
            onClick={() => startEvaluation('diff')}
            disabled={isEvaluating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] hover:opacity-90 text-white transition-colors disabled:opacity-40"
          >
            <Zap size={14} />
            Diff Eval
          </button>
          <button
            onClick={() => startEvaluation('full')}
            disabled={isEvaluating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] transition-colors disabled:opacity-40"
          >
            <RotateCcw size={14} />
            Full Re-eval
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Progress bar */}
      {isEvaluating && evalProgress.total > 0 && (
        <div className="mb-3 p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 shrink-0">
          <div className="flex items-center justify-between mb-2 text-sm">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="text-blue-400 animate-spin" />
              <span>Evaluating PRs...</span>
            </div>
            <span className="text-[var(--color-text-muted)]">
              {evalProgress.completed + evalProgress.failed} / {evalProgress.total}
              {evalProgress.failed > 0 && <span className="text-red-400 ml-1">({evalProgress.failed} failed)</span>}
            </span>
          </div>
          <div className="w-full bg-[var(--color-bg)] rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all"
              style={{ width: `${evalProgress.total > 0 ? ((evalProgress.completed + evalProgress.failed) / evalProgress.total) * 100 : 0}%` }}
            />
          </div>
          {evalCurrent && (
            <div className="mt-2 text-xs text-[var(--color-text-muted)]">
              Current: <span className="font-mono">{evalCurrent.repo}</span> #{evalCurrent.pr_number} {evalCurrent.pr_title}
            </div>
          )}
        </div>
      )}

      {/* Repository list */}
      <div className="flex-1 min-h-0 overflow-auto">
        {repoNames.length > 0 ? (
          <div className="space-y-2">
            {repoNames.map((repo) => {
              const prs = repos[repo] ?? []
              const isExpanded = expandedRepos.has(repo)
              const statusCounts = prs.reduce((acc, pr) => {
                acc[pr.evaluation_status] = (acc[pr.evaluation_status] ?? 0) + 1
                return acc
              }, {} as Record<string, number>)

              return (
                <div key={repo} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
                  <button
                    onClick={() => toggleRepoExpand(repo)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="font-mono font-medium">{repo}</span>
                    <span className="text-[var(--color-text-muted)] text-xs">
                      ({prs.length} {prs.length === 1 ? 'PR' : 'PRs'})
                    </span>
                    <div className="flex items-center gap-1 ml-auto">
                      {Object.entries(statusCounts).map(([status, count]) => (
                        <span key={status} className={cn('px-1.5 py-0.5 rounded text-[10px]', EVAL_STATUS_MAP[status]?.cls ?? '')}>
                          {count}
                        </span>
                      ))}
                    </div>
                  </button>

                  {isExpanded && prs.length > 0 && (
                    <div className="border-t border-[var(--color-border)] divide-y divide-[var(--color-border)]">
                      {prs.map((pr) => {
                        const prKey = `${repo}:${pr.pr_number}`
                        const isDetailOpen = expandedPr === prKey
                        const sj = pr.summary_json

                        return (
                          <div key={pr.pr_number} className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setExpandedPr(isDetailOpen ? null : prKey)}
                                className="text-[var(--color-primary)] hover:underline text-xs font-mono shrink-0"
                              >
                                #{pr.pr_number}
                              </button>
                              <span className="text-xs truncate" title={pr.pr_title ?? ''}>
                                {pr.pr_title ?? ''}
                              </span>
                              <div className="flex items-center gap-1 ml-auto shrink-0">
                                <EvalStatusBadge status={pr.evaluation_status} />
                                {sj?.risk_level && <RiskBadge level={sj.risk_level} />}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {pr.pr_url && (
                                <a href={pr.pr_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] flex items-center gap-0.5">
                                  {pr.pr_author}
                                  <ExternalLink size={9} />
                                </a>
                              )}
                              <span className="text-[10px] text-[var(--color-text-muted)] font-mono flex items-center gap-0.5">
                                {pr.pr_branch} <ArrowRight size={8} /> {pr.pr_base}
                              </span>
                              <CiBadge status={pr.ci_status} />
                              <MergeableBadge state={pr.mergeable_state} />
                              {pr.mergeable_state === 'clean' && (
                                <span className="text-green-400" title="No conflicts">
                                  <CheckCircle size={12} />
                                </span>
                              )}
                              {pr.evaluated_at && (
                                <span className="text-[10px] text-[var(--color-text-muted)]">
                                  Evaluated: {formatDateTime(pr.evaluated_at)}
                                </span>
                              )}
                            </div>

                            {sj?.one_line_summary && (
                              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                                {sj.one_line_summary}
                              </p>
                            )}

                            {/* Expanded detail */}
                            {isDetailOpen && (
                              <div className="mt-3 space-y-3">
                                {/* Structured summary */}
                                {sj && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {sj.purpose && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <Target size={10} /> Purpose
                                        </div>
                                        <p className="text-xs">{sj.purpose}</p>
                                      </div>
                                    )}
                                    {sj.impact_scope && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <Eye size={10} /> Impact Scope
                                        </div>
                                        <p className="text-xs">{sj.impact_scope}</p>
                                      </div>
                                    )}
                                    {sj.key_changes && sj.key_changes.length > 0 && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <Wrench size={10} /> Key Changes
                                        </div>
                                        <ul className="text-xs space-y-0.5">
                                          {sj.key_changes.map((c, i) => (
                                            <li key={i} className="flex items-start gap-1">
                                              <span className="text-[var(--color-text-muted)] shrink-0">-</span>
                                              {c}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {sj.review_points && sj.review_points.length > 0 && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <AlertTriangle size={10} /> Review Points
                                        </div>
                                        <ul className="text-xs space-y-0.5">
                                          {sj.review_points.map((p, i) => (
                                            <li key={i} className="flex items-start gap-1">
                                              <span className="text-[var(--color-text-muted)] shrink-0">-</span>
                                              {p}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {sj.risk_details && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <Shield size={10} /> Risk Details
                                        </div>
                                        <p className="text-xs">{sj.risk_details}</p>
                                      </div>
                                    )}
                                    {sj.dependencies && (
                                      <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] font-medium mb-1">
                                          <GitPullRequestArrow size={10} /> Dependencies
                                        </div>
                                        <p className="text-xs">{sj.dependencies}</p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Full AI summary text */}
                                {pr.summary_text && (
                                  <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                    <span className="text-[10px] text-[var(--color-text-muted)] font-medium">Full AI Analysis:</span>
                                    <div className="mt-1 max-h-64 overflow-auto">
                                      <MarkdownRenderer content={pr.summary_text} className="text-xs" />
                                    </div>
                                  </div>
                                )}

                                {/* Stream content (during evaluation) */}
                                {streamContent[prKey] && (
                                  <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                                    <span className="text-[10px] text-[var(--color-text-muted)] font-medium">AI Activity:</span>
                                    <div className="mt-1 max-h-48 overflow-auto">
                                      <MarkdownRenderer
                                        content={streamContent[prKey]}
                                        isStreaming={evalCurrent?.repo === repo && evalCurrent?.pr_number === pr.pr_number}
                                        className="text-xs"
                                      />
                                    </div>
                                  </div>
                                )}

                                <div className="text-[10px] text-[var(--color-text-muted)]">
                                  Branch: {pr.pr_branch} → {pr.pr_base} · SHA: {pr.head_sha?.slice(0, 7)}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {isExpanded && prs.length === 0 && (
                    <div className="px-4 py-2 text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
                      No open PRs
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16 text-[var(--color-text-muted)]">
            <Eye size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">PR Overview</p>
            <p className="text-xs mt-1">
              全サブモジュールのオープンPRを一覧表示し、AIによるサマリーと評価を提供
            </p>
            <p className="text-xs mt-3 text-[var(--color-text-muted)]/60">
              「PR Update」でPR一覧を取得 → 「Diff Eval」でAI評価を実行
            </p>
          </div>
        )}
      </div>
    </>
  )
}

// --- Judgment Modal ---

function JudgmentModal({
  judgment,
  prInfo: info,
  reviewStep,
  streamContent,
  isStreaming,
  onAction,
  onClose,
}: {
  judgment: WaitingJudgmentData
  prInfo: PrInfo | undefined
  reviewStep: StepData | undefined
  streamContent: string | undefined
  isStreaming: boolean
  onAction: (action: string, message?: string) => Promise<void>
  onClose: () => void
}) {
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [feedbackText, setFeedbackText] = useState('')
  const [showActivity, setShowActivity] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const diagnosisData = judgment.diagnosis ? (() => {
    try { return JSON.parse(judgment.diagnosis!) } catch { return null }
  })() : null

  const reasons: string[] = diagnosisData?.reasons ?? []
  const severity = judgment.reviewSeverity ?? reviewStep?.review_severity ?? null
  const reviewSummary = judgment.reviewSummary ?? reviewStep?.review_summary ?? null
  const ciPassed = judgment.ciPassed
  const mergeable = judgment.mergeable

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      if (selectedAction === 'fix') {
        await onAction('fix', feedbackText)
      } else if (selectedAction) {
        await onAction(selectedAction)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn(
          'px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between shrink-0',
          judgment.isReReview ? 'bg-blue-500/5' : 'bg-purple-500/5',
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center',
              judgment.isReReview ? 'bg-blue-500/20' : 'bg-purple-500/20',
            )}>
              {judgment.isReReview
                ? <RefreshCw size={20} className="text-blue-400" />
                : <MessageSquare size={20} className="text-purple-400" />
              }
            </div>
            <div>
              <h2 className="text-lg font-bold">
                {judgment.isReReview ? '再確認依頼 - AI修正後のレビュー' : '人間による判断が必要です'}
              </h2>
              <p className="text-xs text-[var(--color-text-muted)]">
                {judgment.isReReview
                  ? 'AIが修正を行いました。修正内容を確認の上、判断してください。'
                  : 'AIが自動判断できない項目があります。内容を確認の上、判断してください。'
                }
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors">
            <X size={18} className="text-[var(--color-text-muted)]" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* PR Information */}
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <GitPullRequestArrow size={16} className="text-[var(--color-primary)]" />
              PR情報
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-[var(--color-primary)] font-bold">
                  {judgment.repo} #{judgment.prNumber}
                </span>
                {severity && <SeverityBadge severity={severity} />}
              </div>
              {info?.pr_title && (
                <p className="text-sm font-medium">{info.pr_title}</p>
              )}
              <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
                {info?.pr_author && (
                  <span className="flex items-center gap-1">
                    <User size={12} /> {info.pr_author}
                  </span>
                )}
                {info?.pr_branch && (
                  <span className="flex items-center gap-1 font-mono">
                    <GitBranch size={12} /> {info.pr_branch} → {info.pr_base}
                  </span>
                )}
                {info?.pr_url && (
                  <a href={info.pr_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[var(--color-primary)]">
                    <ExternalLink size={12} /> GitHub
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className={cn(
              'rounded-lg border p-3',
              ciPassed
                ? 'border-green-500/30 bg-green-500/5'
                : judgment.ciTimedOut
                  ? 'border-yellow-500/30 bg-yellow-500/5'
                  : 'border-red-500/30 bg-red-500/5',
            )}>
              <div className="flex items-center gap-2 mb-1">
                {ciPassed
                  ? <CheckCircle size={16} className="text-green-400" />
                  : judgment.ciTimedOut
                    ? <Clock size={16} className="text-yellow-400 animate-pulse" />
                    : <AlertCircle size={16} className="text-red-400" />
                }
                <span className="text-sm font-medium">CI チェック</span>
              </div>
              <p className={cn('text-xs', ciPassed ? 'text-green-400' : judgment.ciTimedOut ? 'text-yellow-400' : 'text-red-400')}>
                {ciPassed ? '全て通過' : judgment.ciTimedOut ? 'CI実行中...' : '未通過 / 失敗あり'}
              </p>
            </div>
            <div className={cn(
              'rounded-lg border p-3',
              mergeable
                ? 'border-green-500/30 bg-green-500/5'
                : 'border-red-500/30 bg-red-500/5',
            )}>
              <div className="flex items-center gap-2 mb-1">
                {mergeable ? <CheckCircle size={16} className="text-green-400" /> : <AlertCircle size={16} className="text-red-400" />}
                <span className="text-sm font-medium">コンフリクト</span>
              </div>
              <p className={cn('text-xs', mergeable ? 'text-green-400' : 'text-red-400')}>
                {mergeable ? 'コンフリクトなし' : 'コンフリクトあり'}
              </p>
            </div>
          </div>

          {/* Judgment Reasons */}
          {reasons.length > 0 && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-yellow-400">
                <AlertTriangle size={16} />
                判断が必要な理由
              </h3>
              <ul className="space-y-1">
                {reasons.map((reason, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="text-yellow-400 mt-0.5">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Fix Summary (re-review only) */}
          {judgment.isReReview && judgment.fixSummary && (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-blue-400">
                <Wrench size={16} />
                AI修正内容
              </h3>
              <div className="max-h-48 overflow-auto">
                <MarkdownRenderer content={judgment.fixSummary} className="text-sm" />
              </div>
            </div>
          )}

          {/* AI Review Summary */}
          {reviewSummary && (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <FileText size={16} className="text-blue-400" />
                AIレビュー結果
              </h3>
              <div className="max-h-64 overflow-auto">
                <MarkdownRenderer content={reviewSummary} className="text-sm" />
              </div>
            </div>
          )}

          {/* AI Activity Log (collapsible) */}
          {streamContent && (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)]">
              <button
                onClick={() => setShowActivity(!showActivity)}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                {showActivity ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <Loader2 size={14} className={cn('text-blue-400', isStreaming && 'animate-spin')} />
                <span className="font-medium">AI処理経過</span>
                {isStreaming && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">処理中</span>}
              </button>
              {showActivity && (
                <div className="px-4 pb-3 max-h-48 overflow-auto border-t border-[var(--color-border)]">
                  <MarkdownRenderer content={streamContent} isStreaming={isStreaming} className="text-xs" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Footer */}
        <div className="px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)] shrink-0 space-y-3">
          <p className="text-sm font-medium">アクションを選択してください:</p>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedAction(selectedAction === 'merge' ? null : 'merge')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all',
                selectedAction === 'merge'
                  ? 'border-green-500 bg-green-500/20 text-green-400'
                  : 'border-[var(--color-border)] hover:border-green-500/50 hover:bg-green-500/5 text-[var(--color-text-muted)]',
              )}
            >
              <Merge size={18} />
              <div className="text-left">
                <div>マージする</div>
                <div className="text-[10px] opacity-70 font-normal">このままマージ実行</div>
                {!ciPassed && (
                  <div className={cn('text-[10px] font-normal mt-0.5', judgment.ciTimedOut ? 'text-yellow-400' : 'text-orange-400')}>
                    {judgment.ciTimedOut
                      ? 'CI実行中 - 完了後のマージを推奨'
                      : 'CI未通過 - 失敗する可能性あり'}
                  </div>
                )}
              </div>
            </button>
            <button
              onClick={() => setSelectedAction(selectedAction === 'skip' ? null : 'skip')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all',
                selectedAction === 'skip'
                  ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400'
                  : 'border-[var(--color-border)] hover:border-yellow-500/50 hover:bg-yellow-500/5 text-[var(--color-text-muted)]',
              )}
            >
              <SkipForward size={18} />
              <div className="text-left">
                <div>スキップ</div>
                <div className="text-[10px] opacity-70 font-normal">今回はマージしない</div>
              </div>
            </button>
            <button
              onClick={() => setSelectedAction(selectedAction === 'fix' ? null : 'fix')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all',
                selectedAction === 'fix'
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                  : 'border-[var(--color-border)] hover:border-blue-500/50 hover:bg-blue-500/5 text-[var(--color-text-muted)]',
              )}
            >
              <Wrench size={18} />
              <div className="text-left">
                <div>修正指示</div>
                <div className="text-[10px] opacity-70 font-normal">AIに修正を依頼</div>
              </div>
            </button>
          </div>

          {/* Fix feedback area */}
          {selectedAction === 'fix' && (
            <div className="space-y-2">
              <label className="text-xs text-[var(--color-text-muted)]">AIへの修正指示:</label>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="修正内容を具体的に記載してください... (例: CIエラーの原因を確認して修正してください)"
                className="w-full px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text)] min-h-[80px] resize-y"
                autoFocus
              />
            </div>
          )}

          {/* Submit button */}
          {selectedAction && (
            <button
              onClick={handleSubmit}
              disabled={submitting || (selectedAction === 'fix' && !feedbackText.trim())}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-40',
                selectedAction === 'merge' && 'bg-green-500 hover:bg-green-600 text-white',
                selectedAction === 'skip' && 'bg-yellow-500 hover:bg-yellow-600 text-black',
                selectedAction === 'fix' && 'bg-blue-500 hover:bg-blue-600 text-white',
              )}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? '送信中...' : (
                <>
                  {selectedAction === 'merge' && 'マージを実行'}
                  {selectedAction === 'skip' && 'スキップを確定'}
                  {selectedAction === 'fix' && '修正指示を送信'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Review & Merge Tab (original) ---

function ReviewMerge() {
  const [config, setConfig] = useState<ReviewConfig | null>(null)
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set())
  const [autoMerge, setAutoMerge] = useState(true)
  const [currentSession, setCurrentSession] = useState<SessionDetail | null>(null)
  const [stepMatrix, setStepMatrix] = useState<Record<string, Record<number, Record<string, StepData>>>>({})
  const [prInfo, setPrInfo] = useState<Record<string, Record<number, PrInfo>>>({})
  const [sessionStatus, setSessionStatus] = useState('')
  const [counters, setCounters] = useState({ total: 0, merged: 0, skipped: 0, failed: 0 })
  const [streamContent, setStreamContent] = useState<Record<string, string>>({})
  const [activeStreams, setActiveStreams] = useState<Set<string>>(new Set())
  const [expandedRepos, setExpandedRepos] = useState<Set<string>>(new Set())
  const [expandedPr, setExpandedPr] = useState<string | null>(null)
  const [waitingJudgments, setWaitingJudgments] = useState<Record<string, WaitingJudgmentData>>({})
  const [judgmentModalKey, setJudgmentModalKey] = useState<string | null>(null)
  const [history, setHistory] = useState<SessionSummary[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const socketRef = useRef<ReturnType<typeof createReviewSocket> | null>(null)
  const sessionIdRef = useRef<number | null>(null)

  // Load config
  useEffect(() => {
    fetchApi<ReviewConfig>('/review/config')
      .then((c) => {
        setConfig(c)
        setSelectedRepos(new Set(c.repos))
      })
      .catch((e) => setError(e.message))
  }, [])

  // REST polling
  const startPolling = useCallback((sessionId: number) => {
    sessionIdRef.current = sessionId
    if (pollingRef.current) clearInterval(pollingRef.current)

    const poll = async () => {
      if (!sessionIdRef.current) return
      try {
        const detail = await fetchApi<SessionDetail>(`/review/${sessionIdRef.current}`)
        setStepMatrix(detail.step_matrix)
        setPrInfo(detail.pr_info)
        setSessionStatus(detail.status)
        setCounters({
          total: detail.total_prs,
          merged: detail.merged_prs,
          skipped: detail.skipped_prs,
          failed: detail.failed_prs,
        })

        // Restore waiting judgments from REST data (in case WebSocket events were missed)
        const restJudgments = extractWaitingJudgments(detail.step_matrix)
        setWaitingJudgments((prev) => {
          const merged = { ...prev }
          for (const [key, data] of Object.entries(restJudgments)) {
            if (!merged[key]) merged[key] = data
          }
          // Remove judgments that are no longer waiting_human
          for (const key of Object.keys(merged)) {
            if (!restJudgments[key]) delete merged[key]
          }
          return merged
        })

        if (['completed', 'failed', 'cancelled', 'interrupted'].includes(detail.status)) {
          setLoading(false)
          stopPolling()
        }
      } catch {
        // ignore
      }
    }

    pollingRef.current = setInterval(poll, 3000)
    poll()
  }, [])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    sessionIdRef.current = null
  }, [])

  // WebSocket
  const connectWs = useCallback(() => {
    if (socketRef.current) return

    const socket = createReviewSocket(
      (event: ReviewEvent) => {
        if (event.type === 'review_step_update' && event.repo && event.pr_number && event.step) {
          const key = `${event.repo}:${event.pr_number}`
          // Update step matrix
          setStepMatrix((prev) => {
            const repo = event.repo!
            const prNum = event.pr_number!
            const step = event.step!
            return {
              ...prev,
              [repo]: {
                ...prev[repo],
                [prNum]: {
                  ...(prev[repo]?.[prNum] ?? {}),
                  [step]: {
                    ...(prev[repo]?.[prNum]?.[step] ?? {} as StepData),
                    status: event.status ?? 'pending',
                    detail: event.detail ?? '',
                    error: event.error ?? '',
                    review_summary: event.review_summary ?? prev[repo]?.[prNum]?.[step]?.review_summary ?? null,
                    review_severity: event.review_severity ?? prev[repo]?.[prNum]?.[step]?.review_severity ?? null,
                    human_question: event.human_question ?? prev[repo]?.[prNum]?.[step]?.human_question ?? null,
                  },
                },
              },
            }
          })
          // Track active streams and clear judgment when resolved
          if (event.status === 'running') {
            setActiveStreams((prev) => new Set(prev).add(key))
          } else if (['success', 'failed', 'skipped'].includes(event.status ?? '')) {
            setActiveStreams((prev) => { const next = new Set(prev); next.delete(key); return next })
            setWaitingJudgments((prev) => {
              if (prev[key]) {
                const { [key]: _, ...rest } = prev
                return rest
              }
              return prev
            })
          }
        } else if (event.type === 'review_session_status') {
          setSessionStatus(event.status ?? '')
          setCounters({
            total: event.total_prs ?? 0,
            merged: event.merged_prs ?? 0,
            skipped: event.skipped_prs ?? 0,
            failed: event.failed_prs ?? 0,
          })
          if (['completed', 'failed', 'cancelled', 'interrupted'].includes(event.status ?? '')) {
            setLoading(false)
            stopPolling()
          }
        } else if (event.type === 'review_discovery_done') {
          setCounters((prev) => ({ ...prev, total: event.total_prs ?? 0 }))
        } else if (event.type === 'review_stream' && event.repo && event.pr_number) {
          const key = `${event.repo}:${event.pr_number}`
          if (event.content) {
            setStreamContent((prev) => ({
              ...prev,
              [key]: (prev[key] ?? '') + event.content!,
            }))
          } else if (event.tool_name && event.tool_input) {
            // Add visual separator between AI comments when tool calls happen
            setStreamContent((prev) => {
              const current = prev[key] ?? ''
              if (!current) return prev
              return { ...prev, [key]: current + '\n\n---\n\n' }
            })
          }
          setActiveStreams((prev) => new Set(prev).add(key))
        } else if (event.type === 'review_human_question' && event.repo && event.pr_number) {
          const key = `${event.repo}:${event.pr_number}`
          // Store full judgment context
          setWaitingJudgments((prev) => ({
            ...prev,
            [key]: {
              repo: event.repo!,
              prNumber: event.pr_number!,
              question: event.human_question ?? '',
              reviewSummary: event.review_summary ?? null,
              reviewSeverity: event.review_severity ?? null,
              ciPassed: event.ci_passed ?? true,
              mergeable: event.mergeable ?? true,
              diagnosis: event.diagnosis ?? null,
              isReReview: event.is_re_review ?? false,
              fixSummary: event.fix_summary ?? null,
              ciTimedOut: event.ci_timed_out ?? false,
            },
          }))
          // Auto-expand repo and open modal
          setExpandedRepos((prev) => new Set(prev).add(event.repo!))
          setJudgmentModalKey(key)
        }
      },
      () => { socketRef.current = null },
    )
    socketRef.current = socket
  }, [stopPolling])

  useEffect(() => {
    return () => {
      socketRef.current?.close()
      stopPolling()
    }
  }, [stopPolling])

  // Actions
  const toggleRepo = (repo: string) => {
    setSelectedRepos((prev) => {
      const next = new Set(prev)
      if (next.has(repo)) next.delete(repo)
      else next.add(repo)
      return next
    })
  }

  const startReview = async () => {
    if (selectedRepos.size === 0) {
      setError('Select at least one repository')
      return
    }

    setError('')
    setLoading(true)
    setStreamContent({})
    setActiveStreams(new Set())
    setWaitingJudgments({})
    setJudgmentModalKey(null)
    setExpandedPr(null)

    try {
      const result = await fetchApi<{ status: string; session_id?: number; message?: string }>(
        '/review/start',
        {
          method: 'POST',
          body: JSON.stringify({
            repos: Array.from(selectedRepos),
            auto_merge: autoMerge,
          }),
        },
      )

      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
        return
      }

      if (result.session_id) {
        const detail = await fetchApi<SessionDetail>(`/review/${result.session_id}`)
        setCurrentSession(detail)
        setStepMatrix(detail.step_matrix)
        setPrInfo(detail.pr_info)
        setSessionStatus(detail.status)
        setWaitingJudgments(extractWaitingJudgments(detail.step_matrix))
        // Expand all repos by default
        setExpandedRepos(new Set(detail.repos))
        startPolling(result.session_id)
        connectWs()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start review')
      setLoading(false)
    }
  }

  const cancelSession = async () => {
    if (!currentSession) return
    try {
      await fetchApi(`/review/${currentSession.id}/cancel`, { method: 'POST' })
    } catch { /* ignore */ }
  }

  const sendAnswer = async (repo: string, prNumber: number, action: string, message?: string) => {
    if (!currentSession) return
    const key = `${repo}:${prNumber}`
    try {
      const result = await fetchApi<{ status: string; message?: string }>(`/review/${currentSession.id}/answer`, {
        method: 'POST',
        body: JSON.stringify({ repo, pr_number: prNumber, action, message: message ?? '' }),
      })
      if (result.status === 'error') {
        setError(result.message ?? '回答の送信に失敗しました')
        return
      }
      // Only clear judgment after confirmed delivery
      setJudgmentModalKey(null)
      setWaitingJudgments((prev) => {
        const { [key]: _, ...rest } = prev
        return rest
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : '回答の送信に失敗しました')
    }
  }

  const loadHistory = async () => {
    try {
      const list = await fetchApi<SessionSummary[]>('/review/list')
      setHistory(list)
      setShowHistory(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load history')
    }
  }

  const loadSession = async (id: number) => {
    try {
      const detail = await fetchApi<SessionDetail>(`/review/${id}`)
      setCurrentSession(detail)
      setStepMatrix(detail.step_matrix)
      setPrInfo(detail.pr_info)
      setSessionStatus(detail.status)
      setWaitingJudgments(extractWaitingJudgments(detail.step_matrix))
      setCounters({
        total: detail.total_prs,
        merged: detail.merged_prs,
        skipped: detail.skipped_prs,
        failed: detail.failed_prs,
      })
      setShowHistory(false)
      setExpandedRepos(new Set(detail.repos))

      if (detail.status === 'running') {
        startPolling(detail.id)
        connectWs()
        setLoading(true)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load session')
    }
  }

  const toggleRepoExpand = (repo: string) => {
    setExpandedRepos((prev) => {
      const next = new Set(prev)
      if (next.has(repo)) next.delete(repo)
      else next.add(repo)
      return next
    })
  }

  const repos = currentSession?.repos ?? Array.from(selectedRepos)
  const steps = config?.steps ?? []
  const isRunning = sessionStatus === 'running' || loading
  const waitingCount = Object.keys(waitingJudgments).length

  // Find the judgment data for the open modal
  const openJudgment = judgmentModalKey ? waitingJudgments[judgmentModalKey] : null
  const openJudgmentPrInfo = openJudgment ? prInfo[openJudgment.repo]?.[openJudgment.prNumber] : undefined
  const openJudgmentReviewStep = openJudgment
    ? stepMatrix[openJudgment.repo]?.[openJudgment.prNumber]?.code_review
    : undefined

  return (
    <>
      {/* Judgment Modal */}
      {openJudgment && judgmentModalKey && (
        <JudgmentModal
          judgment={openJudgment}
          prInfo={openJudgmentPrInfo}
          reviewStep={openJudgmentReviewStep}
          streamContent={streamContent[judgmentModalKey]}
          isStreaming={activeStreams.has(judgmentModalKey)}
          onAction={(action, message) => sendAnswer(openJudgment.repo, openJudgment.prNumber, action, message)}
          onClose={() => setJudgmentModalKey(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {currentSession && sessionStatusBadge(sessionStatus || currentSession.status)}
          {/* Judgment notification badge */}
          {waitingCount > 0 && (
            <button
              onClick={() => {
                const firstKey = Object.keys(waitingJudgments)[0]
                if (firstKey) setJudgmentModalKey(firstKey)
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 transition-colors animate-pulse"
            >
              <Bell size={14} />
              <span className="font-medium">{waitingCount}件の判断待ち</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && currentSession && (
            <button
              onClick={cancelSession}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <XCircle size={14} />
              Cancel
            </button>
          )}
          <button
            onClick={startReview}
            disabled={isRunning || selectedRepos.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] hover:opacity-90 text-white transition-colors disabled:opacity-40"
          >
            <Play size={14} />
            Start Review
          </button>
          <button
            onClick={loadHistory}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] transition-colors"
          >
            <History size={14} />
            History
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Summary Bar */}
      {currentSession && (
        <div className="mb-3 flex items-center gap-4 text-sm shrink-0">
          <span className="text-[var(--color-text-muted)]">Total: <strong>{counters.total}</strong></span>
          <span className="text-green-400">Merged: <strong>{counters.merged}</strong></span>
          <span className="text-yellow-400">Skipped: <strong>{counters.skipped}</strong></span>
          <span className="text-red-400">Failed: <strong>{counters.failed}</strong></span>
          {counters.total > 0 && (
            <span className="text-[var(--color-text-muted)]">
              Waiting: <strong>{counters.total - counters.merged - counters.skipped - counters.failed}</strong>
            </span>
          )}
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowHistory(false)}>
          <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4 max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">Review History</h3>
            {history.length === 0 ? (
              <p className="text-[var(--color-text-muted)] text-sm">No sessions yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => loadSession(s.id)}
                    className="w-full text-left p-3 rounded bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono">Session #{s.id}</span>
                      {sessionStatusBadge(s.status)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                      <span>{s.started_by}</span>
                      <span>{formatDateTime(s.created_at)}</span>
                      <span>{s.total_prs} PRs</span>
                      {s.merged_prs > 0 && <span className="text-green-400">{s.merged_prs} merged</span>}
                      {!s.auto_merge && <span className="text-yellow-500">(manual)</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {/* Config panel */}
        {!currentSession && config && (
          <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Target Repositories</span>
              <div className="flex gap-2 text-xs">
                <button onClick={() => setSelectedRepos(new Set(config.repos))} className="text-[var(--color-primary)] hover:underline">All</button>
                <button onClick={() => setSelectedRepos(new Set())} className="text-[var(--color-text-muted)] hover:underline">None</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {config.repos.map((repo) => (
                <label
                  key={repo}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs border cursor-pointer transition-colors',
                    selectedRepos.has(repo)
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)]',
                  )}
                >
                  <input type="checkbox" checked={selectedRepos.has(repo)} onChange={() => toggleRepo(repo)} className="sr-only" />
                  {repo}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-[var(--color-border)]">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoMerge}
                  onChange={(e) => setAutoMerge(e.target.checked)}
                  className="rounded"
                />
                <span>Auto-merge (clean/minor PRs with CI pass)</span>
              </label>
            </div>
          </div>
        )}

        {/* PR Pipeline Grid */}
        {currentSession && Object.keys(stepMatrix).length > 0 && (
          <div className="space-y-2">
            {repos.map((repo) => {
              const repoPrs = stepMatrix[repo] ?? {}
              const prNums = Object.keys(repoPrs).map(Number)
              const prCount = prNums.length
              const isExpanded = expandedRepos.has(repo)

              return (
                <div key={repo} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
                  {/* Repo header */}
                  <button
                    onClick={() => toggleRepoExpand(repo)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="font-mono font-medium">{repo}</span>
                    <span className="text-[var(--color-text-muted)] text-xs">
                      ({prCount} {prCount === 1 ? 'PR' : 'PRs'})
                    </span>
                  </button>

                  {isExpanded && prCount > 0 && (
                    <div className="border-t border-[var(--color-border)]">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[var(--color-border)]">
                            <th className="text-left px-4 py-1.5 text-[var(--color-text-muted)] font-medium text-xs w-64">PR</th>
                            {steps.map((step) => (
                              <th key={step} className="text-center px-2 py-1.5 text-[var(--color-text-muted)] font-medium text-xs">
                                {STEP_LABELS[step] ?? step}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {prNums.map((prNum) => {
                            const prSteps = repoPrs[prNum] ?? {}
                            const info = prInfo[repo]?.[prNum]
                            const prKey = `${repo}:${prNum}`
                            const isDetailOpen = expandedPr === prKey
                            const reviewStep = prSteps.code_review
                            const mergeStep = prSteps.merge

                            return (
                              <tr key={prNum} className="border-b border-[var(--color-border)] last:border-0">
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setExpandedPr(isDetailOpen ? null : prKey)}
                                      className="text-[var(--color-primary)] hover:underline text-xs font-mono"
                                    >
                                      #{prNum}
                                    </button>
                                    <span className="text-xs truncate max-w-[180px]" title={info?.pr_title ?? ''}>
                                      {info?.pr_title ?? ''}
                                    </span>
                                    {reviewStep?.review_severity && (
                                      <SeverityBadge severity={reviewStep.review_severity} />
                                    )}
                                  </div>
                                  {info?.pr_url && (
                                    <a href={info.pr_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] flex items-center gap-0.5 mt-0.5">
                                      {info.pr_author} · {info.pr_branch}
                                      <ExternalLink size={9} />
                                    </a>
                                  )}

                                  {/* Human decision trigger */}
                                  {mergeStep?.status === 'waiting_human' && (
                                    <button
                                      onClick={() => setJudgmentModalKey(prKey)}
                                      className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-colors animate-pulse"
                                    >
                                      <MessageSquare size={16} className="text-purple-400 shrink-0" />
                                      <div className="flex-1 text-left">
                                        <p className="text-xs font-medium text-purple-300">判断が必要です</p>
                                        <p className="text-[10px] text-purple-400/70">{mergeStep.human_question}</p>
                                      </div>
                                      <span className="text-[10px] text-purple-400 font-medium shrink-0">詳細を確認 →</span>
                                    </button>
                                  )}

                                  {/* Expanded detail */}
                                  {isDetailOpen && (
                                    <div className="mt-2 p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-xs">
                                      {reviewStep?.review_summary && (
                                        <div className="mb-2">
                                          <span className="text-[var(--color-text-muted)] font-medium">AI Review:</span>
                                          <div className="mt-1">
                                            <MarkdownRenderer content={reviewStep.review_summary} className="text-xs" />
                                          </div>
                                        </div>
                                      )}
                                      {streamContent[prKey] && (
                                        <div className="mb-2">
                                          <span className="text-[var(--color-text-muted)] font-medium">AI Activity:</span>
                                          <div className="mt-1 max-h-48 overflow-auto">
                                            <MarkdownRenderer
                                              content={streamContent[prKey]}
                                              isStreaming={activeStreams.has(prKey) && isRunning}
                                              className="text-xs"
                                            />
                                          </div>
                                        </div>
                                      )}
                                      {info && (
                                        <div className="text-[var(--color-text-muted)]">
                                          Branch: {info.pr_branch} → {info.pr_base} · Author: {info.pr_author}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                                {steps.map((step) => {
                                  const s = prSteps[step]
                                  const status = s?.status ?? 'pending'
                                  return (
                                    <td key={step} className={cn('text-center px-2 py-2', stepBgClass(status))} title={s?.detail || s?.error || status}>
                                      <div className="flex items-center justify-center">
                                        <StepIcon status={status} />
                                      </div>
                                      {s?.error && status === 'failed' && (
                                        <div className="text-[10px] text-red-400/80 mt-0.5 truncate max-w-[80px]" title={s.error}>
                                          {s.error}
                                        </div>
                                      )}
                                      {s?.detail && !['pending', 'failed'].includes(status) && (
                                        <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5 truncate max-w-[80px]">
                                          {s.detail}
                                        </div>
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isExpanded && prCount === 0 && (
                    <div className="px-4 py-2 text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
                      No open PRs
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Per-Task Activity Panel */}
        {isRunning && activeStreams.size > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 size={14} className="text-blue-400 animate-spin" />
              <span className="font-medium">処理中のタスク</span>
              <span className="text-[var(--color-text-muted)] text-xs">({activeStreams.size}件)</span>
            </div>
            {Array.from(activeStreams).map((prKey) => {
              const content = streamContent[prKey]
              if (!content) return null
              const [taskRepo, taskPrNum] = prKey.split(':')
              const taskInfo = prInfo[taskRepo]?.[Number(taskPrNum)]
              return (
                <div key={prKey} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
                  <div className="px-4 py-2 border-b border-[var(--color-border)] flex items-center gap-2 text-xs">
                    <Loader2 size={12} className="text-blue-400 animate-spin shrink-0" />
                    <span className="font-mono text-[var(--color-primary)] font-medium">{taskRepo} #{taskPrNum}</span>
                    {taskInfo?.pr_title && (
                      <span className="text-[var(--color-text-muted)] truncate">{taskInfo.pr_title}</span>
                    )}
                  </div>
                  <div className="p-3 max-h-40 overflow-auto">
                    <MarkdownRenderer content={content} isStreaming={true} className="text-xs" />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Empty state */}
        {!currentSession && Object.keys(stepMatrix).length === 0 && !showHistory && (
          <div className="text-center py-16 text-[var(--color-text-muted)]">
            <GitPullRequestArrow size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">Review & Merge Pipeline</p>
            <p className="text-xs mt-1">
              全サブモジュールのオープンPRを自動収集、AIレビュー、CI確認、マージまで一括自動化
            </p>
            <p className="text-xs mt-3 text-[var(--color-text-muted)]/60">
              Start Review でPR収集 → AIレビュー → 自動マージ or 人間判断
            </p>
          </div>
        )}
      </div>
    </>
  )
}

// --- Main component with tabs ---

export function Review() {
  const [activeTab, setActiveTab] = useState<'overview' | 'merge'>('overview')

  return (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {/* Page header with tabs */}
      <div className="flex items-center gap-4 mb-4 shrink-0">
        <GitPullRequestArrow size={22} className="text-[var(--color-primary)]" />
        <div className="flex items-center gap-1 bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-0.5">
          <button
            onClick={() => setActiveTab('overview')}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              activeTab === 'overview'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <Eye size={14} />
              PR Overview
            </span>
          </button>
          <button
            onClick={() => setActiveTab('merge')}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              activeTab === 'merge'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <Merge size={14} />
              Review & Merge
            </span>
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' ? <PrOverview /> : <ReviewMerge />}
    </div>
  )
}
